import axios from 'axios'
import cookie from 'js-cookie'
import { ModeManager } from '~/managers/ModeManager'
import { ConfigManager } from '~/managers/ConfigManager'
import { Notification } from '~/components/Notification'
import { sessionActivity } from '~/helpers/sessionActivity'

import ENDPOINTS from './paths'

function makeRequest(
  url: string,
  method: 'get' | 'post' | 'delete' | 'put',
  additionalHeaders: object = {},
  requestBody: object = {},
  queryStringParams: object = {},
  paramsSerializer?: (params: object) => string
) {
  let domain
  // Cause this is internal proxy on express server we dont need to provide API_HOST to let it call to Express default
  if (ConfigManager.isAppSite()) {
    domain = '/fe/'
  } else if (ConfigManager.isBizSite() || ConfigManager.isRegionalSite()) {
    domain = ModeManager.isSandboxMode() ? '/sandbox/fe/' : '/fe/'
  }

  type RedirectHtml = string
  function extractUrlFromHtml(htmlString: RedirectHtml): string | null {
    const hrefRegex = /href="([^"]*)"/
    const hrefMatch = hrefRegex.exec(htmlString)
    return hrefMatch ? hrefMatch[1].replace(/&amp;/g, '&') : null
  }

  return axios({
    method,
    url: `${domain || ''}${url}`,
    headers: {
      ...additionalHeaders,
    },
    data: requestBody,
    params: queryStringParams,
    withCredentials: true,
    validateStatus: (status) => status >= 200 && status < 400, // NOTE: adding this since axios is handling 304 as error responses, ref: https://github.com/axios/axios/issues/703
    ...(paramsSerializer && { paramsSerializer: { serialize: paramsSerializer } }),
  })
    .then((response) => {
      if (!url.includes('dashboard/swap/rates')) {
        sessionActivity.emit()
      }
      if (response.status === 302 && typeof response.data === 'string') {
        const extractedUrl = extractUrlFromHtml(response.data)
        if (extractedUrl) {
          return { data: extractedUrl }
        }
      }
      return response.data
    })
    .catch((err) => {
      if (err.response?.status) {
        const { status } = err.response
        switch (status) {
          case 401:
            cookie.remove('SESSIONID')
            sessionStorage.clear()
            window.location.replace('/sso_logout?redirect_url=/&skip_id_token_hint=1')
            break
          case 403:
            Notification.error({
              message: 'Error 403: Forbidden',
              description: 'An error occurred when we tried to process your request.',
            })
            break
          case 413:
            Notification.error({
              message: 'Error 413: Payload Too Large',
              description:
                'The file that you tried to upload is too large. Please try again with a smaller file.',
            })
            break
          case 429:
            Notification.error({
              message: 'Error 429: Too Many Requests',
              description:
                'You have sent too many requests in a given amount of time. Please wait a moment and try again.',
            })
            break
          case 500:
            Notification.error({
              message: 'Error 500: Internal Server Error',
              description:
                'The server encountered an unexpected condition which prevented it from fulfilling the request. Please contact our support team if this problem persists.',
            })
            break
          case 503:
            Notification.error({
              message: 'Error 503: Service Unavailable',
              description:
                'The service is temporarily unavailable. Please try again in a few moments.',
            })
            break
          case 504:
            Notification.error({
              message: 'Error 504: Gateway Timeout',
              description: 'The server took too long to respond. Please try again.',
            })
            break
        }
      }

      throw err
    })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ResponsePromise = Promise<any>
// eslint-disable-next-line @typescript-eslint/typedef
function mock(_: ENDPOINTS, __: string = '', mockData = {}): ResponsePromise {
  return Promise.resolve({ data: mockData })
}

function get(
  endpoint: string,
  subRoute: string = '',
  queryStringParams?: object,
  paramsSerializer?: (params: object) => string
): ResponsePromise {
  return makeRequest(
    endpoint + subRoute,
    'get',
    undefined,
    undefined,
    queryStringParams,
    paramsSerializer
  )
}

function post(
  endpoint: string,
  requestBody: object,
  additionalHeaders: object = {},
  subRoute: string = ''
): ResponsePromise {
  return makeRequest(endpoint + subRoute, 'post', additionalHeaders, requestBody)
}

function del(
  endpoint: string,
  additionalHeaders: object = {},
  subRoute: string = '',
  requestBody?: object
): ResponsePromise {
  return makeRequest(endpoint + subRoute, 'delete', additionalHeaders, requestBody)
}

function put(
  endpoint: string,
  requestBody: object,
  additionalHeaders: object = {},
  subRoute: string = ''
): ResponsePromise {
  return makeRequest(endpoint + subRoute, 'put', additionalHeaders, requestBody)
}

export { get, post, del, put, mock }
